import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import PaymentsTabs from '../../../components/admin/PaymentsTabs';
import RafikiPaymentModal from '../../../components/admin/RafikiPaymentModal';
import { formatApiError } from '../../../lib/apiError';
import { useToast } from '../../../hooks/use-toast';
import axios from 'axios';
import {
  ListOrdered, Loader2, Filter, Inbox, Plus, Eye, RefreshCw, X,
  ArrowDownToLine, ArrowUpFromLine, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  accepted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  submitted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  duplicate_ignored: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const badge = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.pending;

// ---- Deposit / Payout form modal (populated from active-conf) --------------
const PaymentFormModal = ({ type, countries, onClose, onDone }) => {
  const { toast } = useToast();
  const isDeposit = type === 'deposit';
  const [form, setForm] = useState({ country: '', provider: '', currency: '', phone_number: '', amount: '', customer_message: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedCountry = countries.find((c) => c.country === form.country);
  const providers = selectedCountry?.providers || [];

  const onCountry = (code) => {
    const c = countries.find((x) => x.country === code);
    setForm((f) => ({ ...f, country: code, currency: c?.currency || '', provider: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const payload = {
        amount: form.amount, currency: form.currency, phone_number: form.phone_number,
        provider: form.provider, country: form.country,
        customer_message: form.customer_message || null,
      };
      await axios.post(`${API_URL}/api/admin/payments/${isDeposit ? 'deposits' : 'payouts'}`, payload);
      toast({ title: isDeposit ? 'Deposit initiated' : 'Payout initiated' });
      onDone();
    } catch (err) {
      setError(formatApiError(err, 'Could not submit'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid={`${type}-modal`}>
        <div className="flex items-center justify-between p-5 border-b border-slate-700 sticky top-0 bg-slate-800">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            {isDeposit ? <ArrowDownToLine className="w-5 h-5 text-emerald-400" /> : <ArrowUpFromLine className="w-5 h-5 text-amber-400" />}
            {isDeposit ? 'New Deposit' : 'New Payout'}
          </h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4" data-testid={`${type}-form`}>
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid={`${type}-form-error`}>
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}
          {countries.length === 0 && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-400 text-sm">
              Provider configuration is unavailable. Connect PawaPay in Settings first.
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">{isDeposit ? 'Phone Number' : 'Recipient Phone'}</label>
            <input type="text" required value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              placeholder="2507XXXXXXXX" data-testid={`${type}-phone-input`}
              className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Country</label>
              <select required value={form.country} onChange={(e) => onCountry(e.target.value)} data-testid={`${type}-country-select`}
                className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500">
                <option value="">Select…</option>
                {countries.map((c) => <option key={c.country} value={c.country}>{c.country}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Provider</label>
              <select required value={form.provider} onChange={(e) => {
                  const p = providers.find((x) => x.provider === e.target.value);
                  setForm((f) => ({ ...f, provider: e.target.value, currency: p?.currency || f.currency }));
                }}
                disabled={!form.country} data-testid={`${type}-provider-select`}
                className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500 disabled:opacity-50">
                <option value="">Select…</option>
                {providers.map((p) => <option key={p.provider} value={p.provider}>{p.display_name || p.provider}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Currency</label>
              <input type="text" readOnly value={form.currency} placeholder="—" data-testid={`${type}-currency-input`}
                className="w-full px-3 py-2.5 bg-slate-900/30 border border-slate-700 rounded-lg text-slate-400 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Amount</label>
              <input type="text" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="100" data-testid={`${type}-amount-input`}
                className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Customer Message {isDeposit && <span className="text-slate-500 font-normal">(optional)</span>}</label>
            <input type="text" value={form.customer_message} onChange={(e) => setForm({ ...form, customer_message: e.target.value })}
              placeholder="Shown to the customer" data-testid={`${type}-message-input`}
              className="w-full px-3 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500" />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white transition-colors">Cancel</button>
            <button type="submit" disabled={saving} data-testid={`${type}-submit-btn`}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Submit {isDeposit ? 'Deposit' : 'Payout'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Transaction details dialog --------------------------------------------
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-700/40 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-white text-right break-all">{value ?? '—'}</span>
  </div>
);

const DetailsDialog = ({ tx, onClose, onRefresh, refreshing }) => {
  if (!tx) return null;
  const fr = tx.failure_reason ? (tx.failure_reason.reason || tx.failure_reason) : null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] flex flex-col" data-testid="tx-details-dialog">
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white capitalize">{tx.type} details</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => onRefresh(tx.id)} disabled={refreshing} data-testid="tx-details-refresh-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50 transition-colors">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {tx.provider === 'rafiki' ? 'Check Status' : 'Refresh Status'}
            </button>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto">
          <div className="mb-3">
            <span className={`px-2.5 py-1 text-xs font-medium rounded border ${badge(tx.status)}`} data-testid="tx-details-status">{tx.status}</span>
          </div>
          <Row label="Amount" value={tx.amount} />
          <Row label="Currency" value={tx.currency} />
          <Row label="Phone" value={tx.phone_number} />
          <Row label="Provider" value={tx.provider === 'rafiki' ? `Rafiki${tx.mmo_provider ? ' · ' + tx.mmo_provider : ''}` : tx.mmo_provider} />
          <Row label={tx.provider === 'rafiki' ? 'Reference ID' : 'PawaPay ID'} value={tx.pawapay_id} />
          <Row label="Customer Message" value={tx.statement_description} />
          <Row label="Failure Reason" value={fr ? (typeof fr === 'string' ? fr : JSON.stringify(fr)) : '—'} />
          <Row label="Created" value={new Date(tx.created_at).toLocaleString()} />
          <Row label="Updated" value={new Date(tx.updated_at).toLocaleString()} />
          <div className="mt-4">
            <p className="text-slate-400 text-sm mb-1.5">Raw Provider Response</p>
            <pre className="bg-slate-900/70 border border-slate-700 rounded-lg p-3 text-xs text-slate-300 overflow-x-auto max-h-56" data-testid="tx-details-raw">
{JSON.stringify(tx.provider_response || {}, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentsTransactions = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState('pawapay');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [countries, setCountries] = useState([]);
  const [formType, setFormType] = useState(null); // 'deposit' | 'payout'
  const [showRafikiPayout, setShowRafikiPayout] = useState(false);
  const [detailTx, setDetailTx] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('provider', providerFilter);
      if (typeFilter) params.append('type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`${API_URL}/api/admin/payments/transactions?${params}`);
      setRows(res.data.transactions);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Error fetching transactions:', e);
    } finally {
      setLoading(false);
    }
  }, [providerFilter, typeFilter, statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // active-conf for the deposit/payout forms (country/provider/currency)
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/payments/connection`)
      .then((r) => { if (r.data?.status === 'connected') setCountries(r.data.countries || []); })
      .catch(() => {});
  }, []);

  const refresh = async (id) => {
    const row = rows.find((r) => r.id === id) || (detailTx?.id === id ? detailTx : null);
    const isRafiki = row?.provider === 'rafiki';
    const url = isRafiki
      ? `${API_URL}/api/admin/payments/rafiki/payouts/${id}/status`
      : `${API_URL}/api/admin/payments/transactions/${id}/refresh`;
    setRefreshingId(id);
    try {
      const res = await axios.post(url);
      setRows((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      if (detailTx?.id === id) setDetailTx(res.data);
      toast({ title: 'Status updated', description: `Now: ${res.data.status}` });
    } catch (e) {
      toast({ title: 'Status check failed', description: formatApiError(e), variant: 'destructive' });
    } finally { setRefreshingId(null); }
  };

  const openDetails = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments/transactions/${id}`);
      setDetailTx(res.data);
    } catch (e) {
      toast({ title: 'Could not load details', description: formatApiError(e), variant: 'destructive' });
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="payments-transactions-title">
            <ListOrdered className="w-6 h-6 text-amber-400" /> Payments
          </h1>
          <p className="text-slate-400">PawaPay payment operations & transaction ledger.</p>
        </div>
        <div className="flex items-center gap-2">
          {providerFilter === 'rafiki' ? (
            <>
              <button disabled title="Rafiki Payin (deposit) is not yet available" data-testid="rafiki-new-deposit-btn"
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400/50 border border-emerald-500/20 font-semibold rounded-lg cursor-not-allowed">
                <ArrowDownToLine className="w-4 h-4" /> New Deposit <span className="text-[10px] uppercase tracking-wide bg-slate-700/60 text-slate-400 px-1.5 py-0.5 rounded">Soon</span>
              </button>
              <button onClick={() => setShowRafikiPayout(true)} data-testid="rafiki-new-payout-btn"
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors">
                <ArrowUpFromLine className="w-4 h-4" /> New Payout
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setFormType('deposit')} data-testid="new-deposit-btn"
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold rounded-lg hover:bg-emerald-500/30 transition-colors">
                <ArrowDownToLine className="w-4 h-4" /> New Deposit
              </button>
              <button onClick={() => setFormType('payout')} data-testid="new-payout-btn"
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors">
                <ArrowUpFromLine className="w-4 h-4" /> New Payout
              </button>
            </>
          )}
        </div>
      </div>

      <PaymentsTabs />

      {/* Provider separation toggle */}
      <div className="flex items-center gap-1 mb-4 bg-slate-800/50 border border-slate-700/50 rounded-lg p-1 w-fit" data-testid="tx-provider-toggle">
        {[
          { key: 'pawapay', label: 'PawaPay' },
          { key: 'rafiki', label: 'Rafiki' },
        ].map((p) => (
          <button
            key={p.key}
            onClick={() => setProviderFilter(p.key)}
            data-testid={`tx-provider-${p.key}`}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              providerFilter === p.key
                ? 'bg-amber-500 text-slate-900'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-5 h-5 text-slate-500" />
        <select data-testid="tx-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500">
          <option value="">All Types</option>
          <option value="deposit">Deposit</option>
          <option value="payout">Payout</option>
          <option value="refund">Refund</option>
        </select>
        <select data-testid="tx-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500">
          <option value="">All Statuses</option>
          {Object.keys(STATUS_CONFIG).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-sm text-slate-400 ml-auto" data-testid="tx-total-count">{total} transactions</span>
      </div>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12" data-testid="tx-empty-state">
            <Inbox className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No transactions yet</p>
            <p className="text-slate-500 text-sm mt-1">No {providerFilter === 'rafiki' ? 'Rafiki' : 'PawaPay'} transactions to display.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  {['Date', 'Type', 'Amount', 'Currency', 'Phone', 'Provider', 'Status', 'Actions'].map((h) => (
                    <th key={h} className={`text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {rows.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-700/20 transition-colors" data-testid={`tx-row-${t.id}`}>
                    <td className="px-6 py-4 text-sm text-slate-300 whitespace-nowrap">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-white capitalize">{t.type}</td>
                    <td className="px-6 py-4 text-sm text-white">{t.amount || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{t.currency || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{t.phone_number || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{t.provider === 'rafiki' ? `Rafiki${t.mmo_provider ? ' · ' + t.mmo_provider : ''}` : (t.mmo_provider || '-')}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${badge(t.status)}`} data-testid={`tx-status-${t.id}`}>{t.status}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => openDetails(t.id)} title="View details" data-testid={`tx-details-${t.id}`}
                          className="p-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"><Eye className="w-4 h-4" /></button>
                        <button onClick={() => refresh(t.id)} disabled={refreshingId === t.id} title={t.provider === 'rafiki' ? 'Check status' : 'Refresh status'} data-testid={`tx-refresh-${t.id}`}
                          className="p-2 bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors">
                          {refreshingId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formType && (
        <PaymentFormModal
          type={formType}
          countries={countries}
          onClose={() => setFormType(null)}
          onDone={() => { setFormType(null); fetchRows(); }}
        />
      )}
      {detailTx && (
        <DetailsDialog tx={detailTx} onClose={() => setDetailTx(null)} onRefresh={refresh} refreshing={refreshingId === detailTx.id} />
      )}
      {showRafikiPayout && (
        <RafikiPaymentModal
          onClose={() => setShowRafikiPayout(false)}
          onDone={() => { setShowRafikiPayout(false); fetchRows(); }}
        />
      )}
    </AdminLayout>
  );
};

export default PaymentsTransactions;
